import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, RotateCcw, Youtube } from "lucide-react";
import { api } from "@/lib/api";
import { abs } from "@/components/yt/ImageSlot";

const inp = "w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white outline-none focus:border-[#FF3B3B]/60";
const TABS = ["Overview", "Style Presets", "Anchor Poses", "Categories & Formats", "Prompts & Limits"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || `item_${Date.now()}`;

function Toggle({ on, onChange, testid }) {
  return (
    <button onClick={() => onChange(!on)} data-testid={testid} className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? "bg-[#22c55e]" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

function PresetEditor({ items, onChange, kind }) {
  const upd = (i, patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const [name, setName] = useState("");
  return (
    <div className="space-y-2" data-testid={`yt-admin-${kind}`}>
      {items.map((x, i) => (
        <div key={x.id} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`yt-admin-${kind}-${x.id}`}>
          <Toggle on={x.enabled !== false} onChange={(v) => upd(i, { enabled: v })} testid={`yt-admin-${kind}-toggle-${x.id}`} />
          <div className="flex-1 space-y-1.5">
            <input value={x.name} onChange={(e) => upd(i, { name: e.target.value })} className={`${inp} py-1.5 font-semibold`} />
            <textarea value={x.prompt} onChange={(e) => upd(i, { prompt: e.target.value })} rows={2} className={`${inp} resize-y text-xs`} />
          </div>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`yt-admin-${kind}-delete-${x.id}`} className="p-1 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${kind === "styles" ? "style" : "pose"} name`} data-testid={`yt-admin-${kind}-new`} className={inp} />
        <button onClick={() => { if (!name.trim()) return; onChange([...items, { id: slug(name), name: name.trim(), prompt: "", enabled: true }]); setName(""); }}
          data-testid={`yt-admin-${kind}-add`} className="flex shrink-0 items-center gap-1 rounded-lg bg-white/10 px-3 text-xs font-bold text-white hover:bg-white/20"><Plus size={13} /> Add</button>
      </div>
    </div>
  );
}

function ListsEditor({ cfg, set }) {
  const [cat, setCat] = useState("");
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Aspect ratios</div>
        {cfg.aspects.map((a, i) => (
          <div key={a.id} className="mb-1.5 flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2" data-testid={`yt-admin-aspect-${a.id}`}>
            <Toggle on={a.enabled !== false} onChange={(v) => set("aspects", cfg.aspects.map((x, j) => (j === i ? { ...x, enabled: v } : x)))} testid={`yt-admin-aspect-toggle-${a.id}`} />
            <span className="w-12 font-mono text-xs text-[#A1A1AA]">{a.id}</span>
            <input value={a.label} onChange={(e) => set("aspects", cfg.aspects.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={`${inp} py-1`} />
          </div>
        ))}
      </div>
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Categories ({cfg.categories.length})</div>
        <div className="flex flex-wrap gap-1.5">
          {cfg.categories.map((c, i) => (
            <span key={c.name} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${c.enabled !== false ? "border-white/20 text-white" : "border-white/10 text-white/30 line-through"}`} data-testid={`yt-admin-cat-${i}`}>
              <button onClick={() => set("categories", cfg.categories.map((x, j) => (j === i ? { ...x, enabled: x.enabled === false } : x)))}>{c.name}</button>
              <button onClick={() => set("categories", cfg.categories.filter((_, j) => j !== i))} className="text-white/30 hover:text-red-400"><Trash2 size={11} /></button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={cat} onChange={(e) => setCat(e.target.value)} placeholder="New category" data-testid="yt-admin-cat-new" className={inp} />
          <button onClick={() => { if (!cat.trim() || cfg.categories.some((c) => c.name === cat.trim())) return; set("categories", [...cfg.categories, { name: cat.trim(), enabled: true }]); setCat(""); }}
            data-testid="yt-admin-cat-add" className="flex shrink-0 items-center gap-1 rounded-lg bg-white/10 px-3 text-xs font-bold text-white hover:bg-white/20"><Plus size={13} /> Add</button>
        </div>
        <p className="mt-2 text-[10px] text-[#666]">Click a category to show / hide it for users.</p>
      </div>
    </div>
  );
}

function PromptsEditor({ cfg, set }) {
  const area = (k, label, rows = 3) => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">{label}</span>
      <textarea value={cfg[k]} onChange={(e) => set(k, e.target.value)} rows={rows} data-testid={`yt-admin-${k}`} className={`${inp} resize-y text-xs`} />
    </label>
  );
  const num = (k, label) => (
    <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">{label}</span>
      <input type="number" value={cfg[k]} onChange={(e) => set(k, e.target.value)} data-testid={`yt-admin-${k}`} className={inp} /></label>
  );
  return (
    <div className="space-y-4">
      {area("base_prompt", "Master design prompt", 4)}
      {area("anchor_prompt", "Anchor face instruction (after 'Image N')")}
      {area("object_prompt", "Object image instruction (after 'Image N')", 2)}
      {area("edit_prompt", "Touch & Edit instruction")}
      <div className="grid grid-cols-3 gap-3">{num("max_objects", "Max object images (≤6)")}{num("title_max", "Title max chars")}{num("tagline_max", "Tagline max chars")}</div>
    </div>
  );
}

function Overview({ st }) {
  if (!st) return <Loader2 className="animate-spin text-[#FF3B3B]" />;
  const tiles = [["Designs", st.total], ["Successful", st.success], ["Failed", st.failed], ["Touch & Edits", st.edits], ["Waiting review", st.in_review]];
  return (
    <div>
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        {tiles.map(([l, v]) => <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-4"><div className="text-[11px] text-[#A1A1AA]">{l}</div><div className="font-display text-2xl font-black text-white" data-testid={`yt-admin-stat-${l.toLowerCase().replace(/\W+/g, "-")}`}>{v}</div></div>)}
      </div>
      <p className="mb-3 text-xs text-[#A1A1AA]">Every generated thumbnail is sent to <b className="text-white">Design Review Library</b> (Instagram group) for moderation — approve and add the best to the Instagram template gallery. Access & Pro rollout: Feature Control → <span className="font-mono">youtube_thumbnail_designer</span> and <span className="font-mono">yt_thumb_*</span>.</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {st.recent.map((g) => (
          <div key={g.id} className="overflow-hidden rounded-xl border border-white/10" data-testid={`yt-admin-recent-${g.id}`}>
            {g.result_url ? <img src={abs(g.result_url)} alt="" className="aspect-video w-full bg-black object-contain" /> : <div className="flex aspect-video items-center justify-center bg-red-500/10 px-2 text-center text-[10px] text-red-300">{(g.error || "failed").slice(0, 90)}</div>}
            <div className="p-2 text-[10px] text-[#A1A1AA]"><div className="truncate font-bold text-white">{g.title || "—"}</div>{g.user_email} · {g.kind} · {g.aspect}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function YtThumbAdmin() {
  const [tab, setTab] = useState("Overview");
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [st, setSt] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.ytAdminConfig().then((d) => { setCfg(d.config); setDefaults(d.defaults); }).catch(() => toast.error("Could not load thumbnail config"));
    api.ytAdminStats().then(setSt).catch(() => {});
  }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const d = await api.ytAdminSaveConfig(cfg); setCfg(d.config); toast.success("Saved — live for users now"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const resetKeys = { "Style Presets": ["styles"], "Anchor Poses": ["poses"], "Categories & Formats": ["aspects", "categories"], "Prompts & Limits": ["base_prompt", "anchor_prompt", "object_prompt", "edit_prompt", "max_objects", "title_max", "tagline_max"] }[tab];
  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF3B3B]" /></div>;
  return (
    <div data-testid="yt-thumb-admin">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h2 className="flex items-center gap-2 font-display text-lg font-black text-white"><Youtube size={18} className="text-[#FF3B3B]" /> YouTube Thumbnail Designer</h2>
          <p className="text-xs text-[#A1A1AA]">Formats, categories, style presets, anchor poses, prompts and limits. Changes apply to users as soon as you save.</p>
        </div>
        {resetKeys && <button onClick={() => resetKeys.forEach((k) => set(k, defaults[k]))} data-testid="yt-admin-reset" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white"><RotateCcw size={13} /> Reset section</button>}
        {tab !== "Overview" && <button onClick={save} disabled={saving} data-testid="yt-admin-save" className="flex items-center gap-1.5 rounded-lg bg-[#FF3B3B] px-4 py-1.5 text-xs font-bold text-white disabled:opacity-50"><Save size={13} /> {saving ? "Saving…" : "Save changes"}</button>}
      </div>
      <div className="mb-5 flex flex-wrap gap-1.5">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`yt-admin-tab-${t.toLowerCase().replace(/\W+/g, "-")}`} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === t ? "bg-white text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{t}</button>)}
      </div>
      {tab === "Overview" && <Overview st={st} />}
      {tab === "Style Presets" && <PresetEditor items={cfg.styles} onChange={(v) => set("styles", v)} kind="styles" />}
      {tab === "Anchor Poses" && <PresetEditor items={cfg.poses} onChange={(v) => set("poses", v)} kind="poses" />}
      {tab === "Categories & Formats" && <ListsEditor cfg={cfg} set={set} />}
      {tab === "Prompts & Limits" && <PromptsEditor cfg={cfg} set={set} />}
    </div>
  );
}
